// src/pages/home-landing/components/ProgressSection.jsx

import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import StockCounter from '../../../components/ui/StockCounter';
import { supabase } from '../../../supabaseClient'; // 👈 Importamos el cliente de Supabase

const ProgressSection = ({ className = '' }) => {
  // 1. ESTADOS PARA LA ANIMACIÓN Y DATOS DE PROGRESO
  const [isAnimating, setIsAnimating] = useState(false);
  const [progressData, setProgressData] = useState(null); // 👈 Estado para los datos calculados
  const [loading, setLoading] = useState(true); // 👈 Estado de carga
  const [error, setError] = useState(null); // 👈 Estado de error

  // Valores por defecto (se usarán si raffleData es null o incompleto)
  const defaultTicketPrice = 50; 
  const defaultTotalTickets = 5000;

  // Función para cargar los datos del sorteo activo y calcular progreso
  const fetchProgressData = async () => {
    try {
      setLoading(true);

      // 1. CONSULTA DIRECTA A LA FUNCIÓN SQL
      const { data, error } = await supabase
        .rpc('get_sorteo_progress')
        .single();

      if (error) throw error;
      if (!data || data.total_boletos === null) {
        setProgressData(null);
        return;
      }

      const soldTickets = Number(data.boletos_vendidos);
      const availableTickets = Number(data.boletos_disponibles);
      const totalTickets = Number(data.total_boletos);
      const totalRecaudado = Number(data.recaudado);

      const soldPercentage = totalTickets > 0 ? (soldTickets / totalTickets) * 100 : 0;

      // Obtener precio por boleto (opcional, si necesitas mostrarlo)
      const { data: raffle } = await supabase
        .from('raffles')
        .select('precio_boleto, fecha_sorteo')
        .eq('estado', 'activo')
        .single();

      const drawDate = raffle?.fecha_sorteo
        ? new Date(raffle.fecha_sorteo).toLocaleDateString('es-VE')
        : 'N/D';

      console.log('Progreso:', { soldTickets, availableTickets, totalTickets, totalRecaudado });

      setProgressData({
        totalTickets,
        soldTickets,
        remainingTickets: availableTickets,
        availableTickets,
        soldPercentage,
        ticketPrice: raffle?.precio_boleto || 50,
        drawDate,
        totalRecaudado,
      });
    } catch (err) {
      console.error('Error al cargar progreso:', err);
      setError(err.message || 'Error desconocido');
    } finally {
      setLoading(false);
    }
  };

  // Efecto para cargar los datos del sorteo activo y calcular progreso al montar el componente
  useEffect(() => {
    fetchProgressData();

    // Suscripción realtime a 'tickets' para updates
    const channel = supabase
      .channel('purchases-changes') // 👈 Puedes cambiar este nombre si lo deseas, pero no es crítico
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'tickets', // 👈 CAMBIO: 'purchases' → 'tickets'
        },
        () => {
          fetchProgressData(); // Recarga al cambiar
        }
      )
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, []);

  // EFECTO DE ANIMACIÓN SIMPLE (Para mantener el pulso visual)
  useEffect(() => {
    // Simular un pulso de actualización para el efecto visual de "tiempo real"
    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => setIsAnimating(false), 800);
    }, 45000); // Cada 45 segundos

    return () => clearInterval(interval);
  }, []);

  // Si hay error, mostramos un mensaje
  if (error) {
    return (
      <div className={`bg-background py-16 text-center ${className}`}>
        <p className="text-xl text-error">⚠️ Error al cargar el progreso del sorteo.</p>
        <p className="mt-2 text-sm text-gray-600">{error}</p>
      </div>
    );
  }

  // Si está cargando, mostramos un placeholder
  if (loading) {
    return (
      <div className={`bg-background py-16 text-center ${className}`}>
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-secondary mx-auto"></div>
        <p className="mt-4 text-lg">Cargando progreso del sorteo...</p>
      </div>
    );
  }

  // Si no hay datos (no hay sorteo activo), mostramos un mensaje
  if (!progressData) {
    return (
      <div className={`bg-background py-16 text-center ${className}`}>
        <p className="text-xl text-warning">ℹ️ No hay ningún sorteo activo en este momento.</p>
      </div>
    );
  }

  // ***************************************************************
  // 3. LÓGICA DE ESTILOS Y MENSAJES (Mantenida de tu código original)
  // ***************************************************************
  const getProgressColor = () => {
    if (progressData.soldPercentage >= 90) return 'from-error to-warning';
    if (progressData.soldPercentage >= 70) return 'from-warning to-secondary';
    return 'from-secondary to-success';
  };

  const getStatusMessage = () => {
    if (progressData.soldPercentage >= 95) return '¡Últimos boletos disponibles!';
    if (progressData.soldPercentage >= 80) return 'Stock limitado - ¡Compra ahora!';
    if (progressData.soldPercentage >= 50) return 'Más del 50% vendido';
    return 'Boletos disponibles';
  };

  const getStatusIcon = () => {
    if (progressData.soldPercentage >= 95) return 'AlertTriangle';
    if (progressData.soldPercentage >= 80) return 'Clock';
    return 'TrendingUp';
  };

  const getStatusColor = () => {
    if (progressData.soldPercentage >= 95) return 'text-error';
    if (progressData.soldPercentage >= 80) return 'text-warning';
    return 'text-success';
  };

  return (
    <div className={`bg-background py-16 ${className}`}>
      <div className="max-w-7xl mx-auto px-4 lg:px-6">
        
        {/* Section Header */}
        <div className="text-center mb-12">
          {/* Ajustado bg-secondary/10 -> bg-secondary/20 */}
          <div className="inline-flex items-center space-x-2 bg-secondary/20 border border-secondary/30 rounded-full px-4 py-2 mb-6">
            <Icon name="BarChart3" size={16} className="text-secondary" />
            <span className="text-sm font-caption text-secondary font-medium">
              Progreso en Tiempo Real
            </span>
          </div>
          
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
            Estado del Sorteo
          </h2>
          {/* text-text-secondary -> text-muted-foreground (Se ve bien sobre el fondo oscuro) */}
          <p className="text-lg text-muted-foreground font-body max-w-2xl mx-auto">
            Observa en tiempo real cuántos boletos se han vendido y cuántos quedan disponibles
          </p>
        </div>

        {/* Main Progress Card (FONDO CLARO: bg-card) */}
        <div className="bg-card rounded-2xl border border-golden/20 shadow-luxury p-8 mb-8">
          
          {/* Status Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center space-x-3">
              {/* Ajustado bg-secondary/10 -> bg-secondary/20 */}
              <div className={`p-3 rounded-xl bg-secondary/20 ${isAnimating ? 'animate-golden-pulse' : ''}`}>
                <Icon 
                  name={getStatusIcon()} 
                  size={24} 
                  className={getStatusColor()} 
                />
              </div>
              <div>
                {/* CORRECCIÓN: text-foreground -> text-black */}
                <h3 className="text-xl font-heading font-bold text-black">
                  {getStatusMessage()}
                </h3>
                {/* CORRECCIÓN: text-foreground -> text-black */}
                <p className="text-sm text-black font-caption">
                  Actualizado hace pocos segundos
                </p>
              </div>
            </div>
            
            <div className="text-right">
              {/* CORRECCIÓN: text-foreground -> text-black */}
              <div className="text-3xl font-heading font-bold text-black">
                {progressData.soldPercentage?.toFixed(1)}%
              </div>
              {/* CORRECCIÓN: text-foreground -> text-black */}
              <div className="text-sm text-black font-caption">
                Vendido
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="space-y-4 mb-8">
            {/* CORRECCIÓN: text-foreground -> text-black */}
            <div className="flex justify-between text-sm font-caption text-black">
              <span>0 boletos</span>
              <span>{progressData.totalTickets?.toLocaleString()} boletos</span>
            </div>
            
            <div className="relative">
              <div className="w-full bg-muted/30 rounded-full h-6 overflow-hidden">
                <div 
                  className={`h-full bg-gradient-to-r ${getProgressColor()} transition-all duration-1000 ease-out relative ${
                    isAnimating ? 'animate-golden-pulse' : ''
                  }`}
                  style={{ width: `${progressData.soldPercentage}%` }}
                >
                  {/* Shimmer Effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer"></div>
                </div>
              </div>
              
              {/* Progress Marker */}
              <div 
                className="absolute top-0 h-6 w-1 bg-foreground rounded-full transform -translate-x-0.5 transition-all duration-1000"
                style={{ left: `${progressData.soldPercentage}%` }}
              />
            </div>
          </div>

          {/* Statistics Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            
            {/* Sold Tickets */}
            <div className="bg-muted/20 rounded-xl p-6 text-center">
              <div className="flex items-center justify-center space-x-2 mb-3">
                <Icon name="CheckCircle" size={20} className="text-success" />
                {/* CORRECCIÓN: text-foreground -> text-black */}
                <span className="font-heading font-semibold text-black">
                  Boletos Vendidos
                </span>
              </div>
              <div className={`text-3xl font-heading font-bold text-success ${
                isAnimating ? 'animate-golden-pulse' : ''
              }`}>
                {progressData.soldTickets?.toLocaleString()}
              </div>
              {/* CORRECCIÓN: text-foreground -> text-black */}
              <div className="text-sm text-black font-caption mt-1">
                de {progressData.totalTickets?.toLocaleString()} totales
              </div>
            </div>

            {/* Remaining Tickets */}
            <div className="bg-muted/20 rounded-xl p-6 text-center">
              <div className="flex items-center justify-center space-x-2 mb-3">
                <Icon name="Clock" size={20} className="text-secondary" />
                {/* CORRECCIÓN: text-foreground -> text-black */}
                <span className="font-heading font-semibold text-black">
                  Disponibles
                </span>
              </div>
              <div className="text-3xl font-heading font-bold text-secondary">
                {progressData.remainingTickets?.toLocaleString()}
              </div>
              {/* CORRECCIÓN: text-foreground -> text-black */}
              <div className="text-sm text-black font-caption mt-1">
                boletos restantes
              </div>
            </div>

            {/* Revenue Generated */}
            <div className="bg-muted/20 rounded-xl p-6 text-center">
              <div className="flex items-center justify-center space-x-2 mb-3">
                <Icon name="DollarSign" size={20} className="text-golden" />
                {/* CORRECCIÓN: text-foreground -> text-black */}
                <span className="font-heading font-semibold text-black">
                  Recaudado
                </span>
              </div>
              <div className="text-3xl font-heading font-bold text-golden">
                Bs. {progressData.totalRecaudado?.toLocaleString('es-VE', { minimumFractionDigits: 2 })}
              </div>
              {/* CORRECCIÓN: text-foreground -> text-black */}
              <div className="text-sm text-black font-caption mt-1">
                en ventas totales
              </div>
            </div>
          </div>
        </div>

        {/* Stock Counter Component */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          
          {/* CAMBIO CLAVE: Envolvemos StockCounter en un div con text-black */}
          <div className="text-black"> 
            <StockCounter 
              initialStock={progressData.remainingTickets}
              maxStock={progressData.totalTickets}
              showProgress={true}
              className="h-full"
            />
          </div>
          
          {/* Quick Stats (FONDO CLARO: bg-card) */}
          <div className="bg-card rounded-xl border border-border p-6 shadow-luxury">
            {/* CORRECCIÓN: text-card-foreground -> text-black */}
            <h3 className="font-heading font-bold text-black mb-6 flex items-center space-x-2">
              <Icon name="Activity" size={20} className="text-secondary" />
              <span>Estadísticas Rápidas</span>
            </h3>
            
            <div className="space-y-4">
              {/* CORRECCIÓN: text-foreground -> text-black */}
              <div className="flex justify-between items-center py-2 border-b border-border">
                <span className="text-black font-caption">Precio por boleto</span>
                {/* CORRECCIÓN: text-foreground -> text-black */}
                <span className="font-mono font-bold text-black">
                  Bs. {progressData.ticketPrice?.toLocaleString('es-VE', { minimumFractionDigits: 2 })}
                </span>
              </div>
              
              <div className="flex justify-between items-center py-2 border-b border-border">
                <span className="text-black font-caption">Velocidad de venta</span>
                {/* CORRECCIÓN: text-foreground -> text-black */}
                <span className="font-mono font-bold text-black">~3 boletos/min</span>
              </div>
              
              <div className="flex justify-between items-center py-2 border-b border-border">
                <span className="text-black font-caption">Tiempo estimado agotamiento</span>
                {/* El valor del tiempo (text-warning) se mantiene ya que es un color funcional visible */}
                <span className="font-mono font-bold text-warning">
                  {progressData.remainingTickets > 0 ? `${Math.ceil(progressData.remainingTickets / 3)} minutos` : 'Agotado'}
                </span>
              </div>
              
              <div className="flex justify-between items-center py-2">
                <span className="text-black font-caption">Fecha del sorteo</span>
                {/* CORRECCIÓN: text-foreground -> text-black */}
                <span className="font-mono font-bold text-black">
                  {progressData.drawDate}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgressSection;